import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon, { IconType } from 'components/Icon';
import { Loader } from 'components/Widget/Loader';
import { formatFileSize } from 'utils';
import * as S from './styles';

export type LightboxItem =
	| { type: 'image'; url: string }
	| { type: 'text'; url: string; filename: string; size?: number }
	| { type: 'pdf'; url: string; filename: string };

// Matches filenames generated by detectPastedFileType + deduplicateFilename
const PASTED_FILENAME_RE = /^pasted(-\d+)?\.\w+$/;

const isPastedFilename = (filename: string) => PASTED_FILENAME_RE.test(filename);

interface LightboxProps {
	items: LightboxItem[];
	startIndex?: number;
	onClose: () => void;

	// Supplying either one adds a bar under the content. A caller that passes
	// neither gets the plain viewer: content and a close button, nothing else.
	notice?: React.ReactNode;
	actions?: React.ReactNode;
}

// Bar vocabulary, exported so every caller that supplies actions renders them
// identically instead of restyling buttons against the dark overlay itself.
export const LightboxNotice = S.Notice;
export const LightboxActionButton = S.ActionButton;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const Lightbox = ({ items, startIndex = 0, onClose, notice, actions }: LightboxProps) => {
	const [currentIndex, setCurrentIndex] = useState(() =>
		clamp(startIndex, 0, Math.max(items.length - 1, 0))
	);

	// Image-specific loading state
	const [isLoading, setIsLoading] = useState(true);
	const [imageError, setImageError] = useState(false);
	const [prevUrl, setPrevUrl] = useState<string | null>(null);

	// Text-specific loading state
	const [textContent, setTextContent] = useState<string | null>(null);
	const [textLoading, setTextLoading] = useState(false);
	const [textError, setTextError] = useState(false);

	const item = items[currentIndex] as LightboxItem | undefined;
	const currentUrl = item?.type === 'image' ? item.url : null;

	// Synchronous setState-during-render: React supports calling setState during the render
	// pass when deriving state from props. This resets isLoading/imageError *before* the DOM
	// commits, avoiding a race condition where a cached image's onLoad fires before a
	// useEffect-based reset (which runs after paint) — leaving the lightbox stuck on a loader.
	if (currentUrl !== prevUrl) {
		setPrevUrl(currentUrl);
		if (currentUrl) {
			setIsLoading(true);
			setImageError(false);
		}
	}

	// Re-clamp when items or startIndex change externally
	const maxIndex = Math.max(items.length - 1, 0);
	if (items.length > 0 && currentIndex > maxIndex) {
		setCurrentIndex(maxIndex);
	}

	const canGoPrev = currentIndex > 0;
	const canGoNext = currentIndex < items.length - 1;
	const showNav = items.length > 1;

	const onPrev = useCallback(() => {
		setCurrentIndex((i) => Math.max(i - 1, 0));
	}, []);

	const onNext = useCallback(() => {
		setCurrentIndex((i) => Math.min(i + 1, items.length - 1));
	}, [items.length]);

	// Fetch text content when navigating to a text item.
	// Uses proxy=true so the attachment route serves content directly with CORS
	// headers instead of redirecting to a signed GCS URL (which fails cross-origin).
	useEffect(() => {
		if (!item || item.type !== 'text') {
			setTextContent(null);
			setTextLoading(false);
			setTextError(false);
			return;
		}

		const controller = new AbortController();
		setTextLoading(true);
		setTextError(false);
		setTextContent(null);

		const proxyUrl = item.url + (item.url.includes('?') ? '&' : '?') + 'proxy=true';
		fetch(proxyUrl, { signal: controller.signal })
			.then((r) => {
				if (!r.ok) throw new Error(`HTTP ${r.status}`);
				return r.text();
			})
			.then((text) => {
				setTextContent(text);
				setTextLoading(false);
			})
			.catch((err) => {
				if (err.name !== 'AbortError') {
					Sentry.captureException(err);
					setTextError(true);
					setTextLoading(false);
				}
			});

		return () => controller.abort();
	}, [item?.type === 'text' ? item.url : null]);

	useEffect(() => {
		if (!items.length) return;

		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				e.stopPropagation();
				onClose();
			} else if (e.key === 'ArrowLeft') {
				e.stopPropagation();
				e.preventDefault();
				onPrev();
			} else if (e.key === 'ArrowRight') {
				e.stopPropagation();
				e.preventDefault();
				onNext();
			}
		};

		window.addEventListener('keydown', onKeyDown, true);
		return () => {
			window.removeEventListener('keydown', onKeyDown, true);
		};
	}, [items.length, onClose, onPrev, onNext]);

	if (!items.length || !item) {
		return null;
	}

	const onImageLoad = () => {
		setIsLoading(false);
	};

	const onImageError = () => {
		setIsLoading(false);
		setImageError(true);
	};

	const onOverlayClick = (e: React.MouseEvent) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	// Portals still bubble contextmenu through the React tree (e.g. to chat bubbles). Stop
	// propagation so the native image menu works; do not preventDefault on the image.
	// This is done so the regular browser menu with things like "Save image as..." works.
	const onIsolateContextMenu = (e: React.MouseEvent) => {
		e.stopPropagation();
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const hasActionBar = !!notice || !!actions;

	const renderContent = () => {
		if (item.type === 'image') {
			return (
				<>
					{isLoading && (
						<S.LoadingContainer>
							<Loader size={48} />
						</S.LoadingContainer>
					)}
					{imageError ? (
						<S.LoadingContainer>
							<div style={{ color: 'white' }}>Failed to load image</div>
						</S.LoadingContainer>
					) : (
						<S.Image
							src={item.url}
							alt="Attachment preview"
							onLoad={onImageLoad}
							onError={onImageError}
							onContextMenu={onIsolateContextMenu}
							style={{ display: isLoading ? 'none' : 'block' }}
							$compact={hasActionBar}
						/>
					)}
				</>
			);
		}

		if (item.type === 'text') {
			if (textLoading) {
				return (
					<S.LoadingContainer>
						<Loader size={48} />
					</S.LoadingContainer>
				);
			}
			if (textError) {
				return (
					<S.LoadingContainer>
						<div style={{ color: 'white' }}>Failed to load file</div>
					</S.LoadingContainer>
				);
			}
			if (textContent != null) {
				const isPasted = isPastedFilename(item.filename);
				const displayName = isPasted ? 'Pasted content' : item.filename;
				const lineCount = textContent.split('\n').length;
				const sizeBytes = item.size ?? new Blob([textContent]).size;

				const metaParts = [
					formatFileSize(sizeBytes),
					`${lineCount} line${lineCount !== 1 ? 's' : ''}`,
				];
				if (isPasted) metaParts.push('Formatting may be inconsistent from source');

				return (
					<S.TextPreview>
						<S.TextHeader>
							<S.TextFilename>{displayName}</S.TextFilename>
							<S.TextMeta>{metaParts.join(' \u2022 ')}</S.TextMeta>
						</S.TextHeader>
						<S.TextContent>{textContent}</S.TextContent>
					</S.TextPreview>
				);
			}
			return null;
		}

		if (item.type === 'pdf') {
			return <S.PdfPreview src={item.url} title={item.filename} />;
		}

		return null;
	};

	const renderBody = () => {
		const content = (
			<S.ContentContainer onContextMenu={onIsolateContextMenu}>
				{renderContent()}
			</S.ContentContainer>
		);

		if (!hasActionBar) return content;

		return (
			<S.Stage>
				{content}
				<S.FloatingBar>
					{notice}
					{actions && <S.ActionRow>{actions}</S.ActionRow>}
				</S.FloatingBar>
			</S.Stage>
		);
	};

	return createPortal(
		<S.Overlay onClick={onOverlayClick} onContextMenu={onIsolateContextMenu}>
			<S.CloseButton onClick={onClose} aria-label="Close attachment preview">
				<Icon type={IconType.CLOSE} width={24} height={24} />
			</S.CloseButton>
			{showNav && canGoPrev && (
				<S.NavButton $side="left" onClick={onPrev} aria-label="Previous attachment">
					<Icon type={IconType.LINE_ARROW_LEFT} width={24} height={24} />
				</S.NavButton>
			)}
			{renderBody()}
			{showNav && canGoNext && (
				<S.NavButton $side="right" onClick={onNext} aria-label="Next attachment">
					<Icon type={IconType.LINE_ARROW_RIGHT} width={24} height={24} />
				</S.NavButton>
			)}
		</S.Overlay>,
		document.body
	);
};

export default Lightbox;
